import { CREATE_DATA_OBJECT, GET_BY_ID_LIST, RECEIVE_DATA_OBJECTS } from '@inpart-io/devtools-vue';
import { DataType, evaluationsManager } from '@inpart-io/padme-iap';
import { type IEvaluation, type IPrivateTask, IPrivateTaskStatus } from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { SAVE_SEARCH_RESULT } from '@/store/base/search/actions';
import { GET_SEARCH_RESULT_ID_LIST } from '@/store/base/search/getters';
import { store } from '@/store/index';
import { PRIVATE_TASK_MODULE } from '@/store/private-task-module/module';

export const CREATE_EVALUATION = 'createEvaluation';
export const ADD_INVITEES = 'addInvitees';
export const REMOVE_INVITEES = 'removeInvitees';
export const SEND_REMINDER = 'sendReminder';
export const CHANGE_STATUS = 'changeStatus';
export const STORE_DATA_OBJECTS = 'storeDataObjects';
export const MARK_DONE = 'markDone';
export const RESUME_PARTICIPATION = 'resumeParticipation';

export const actions: ActionTree<IEvaluation, unknown> = {
    async [CREATE_EVALUATION](
        context,
        { key, dataObjectList, properties }: { key: string; dataObjectList: IEvaluation[]; properties?: string }
    ): Promise<number[]> {
        const newEvaluationIds: number[] = await context.dispatch(CREATE_DATA_OBJECT, { dataObjectList, properties });
        if (newEvaluationIds?.length > 0) {
            const taskIdList: number[] = store.getters[`${PRIVATE_TASK_MODULE}/${GET_SEARCH_RESULT_ID_LIST}`](key) ?? [];
            const evaluations: IEvaluation[] = context.getters[GET_BY_ID_LIST](newEvaluationIds);
            evaluations.forEach((evaluation: IEvaluation) => {
                if (Number.isInteger(evaluation.onTask)) {
                    taskIdList.push(evaluation.onTask as number);
                } else {
                    taskIdList.push((evaluation.onTask as IPrivateTask).dobjId);
                }
            });
            void store.dispatch(`${PRIVATE_TASK_MODULE}/${SAVE_SEARCH_RESULT}`, { key, idList: taskIdList });
        }
        return newEvaluationIds;
    },

    async [ADD_INVITEES](context, { evaluationId, userIds, properties }: { evaluationId: number; userIds: number[]; properties?: string }) {
        const result: IEvaluation = await evaluationsManager.addInvitees(evaluationId, userIds, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },

    async [REMOVE_INVITEES](context, { evaluationId, userIds, properties }: { evaluationId: number; userIds: number[]; properties?: string }) {
        const result: IEvaluation = await evaluationsManager.removeInvitees(evaluationId, userIds, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },

    async [SEND_REMINDER](context, { evaluationId, properties }: { evaluationId: number; properties?: string }) {
        const result: IEvaluation = await evaluationsManager.sendReminder(evaluationId, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },

    async [CHANGE_STATUS](
        context,
        { dobjId, status, notify, message, properties }: { dobjId: number; status: IPrivateTaskStatus; notify: boolean; message: string; properties?: string }
    ) {
        let result: IEvaluation;
        if (status === IPrivateTaskStatus.ACTIVE) {
            result = await evaluationsManager.changeStatusToActive(dobjId, notify, message, { properties });
        } else {
            if (status === IPrivateTaskStatus.COMPLETED) {
                result = await evaluationsManager.changeStatusToCompleted(dobjId, notify, message, { properties });
            }
        }
        if (result !== undefined) {
            void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
        }
    },
    async [MARK_DONE](context, { dobjId }: { dobjId: number }) {
        const result: IEvaluation = await evaluationsManager.markDone(dobjId);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },
    async [RESUME_PARTICIPATION](context, { dobjId }: { dobjId: number }) {
        const result: IEvaluation = await evaluationsManager.resumeParticipation(dobjId);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },
    [STORE_DATA_OBJECTS](context, { dataObjectList }: { dataObjectList: IEvaluation[] }) {
        const evaluations: IEvaluation[] = [];
        const tasks: IPrivateTask[] = [];
        dataObjectList.forEach((evaluation) => {
            let _evaluation = Object.assign({}, evaluation);
            // Extract task object if necessary
            if (evaluation?.onTask instanceof Object && evaluation?.onTask?.dobjId !== undefined) {
                tasks.push(Object.assign({}, evaluation.onTask, { '@evaluations': [evaluation.dobjId], '@type': DataType.PrivateTask }));
                _evaluation = Object.assign({}, evaluation, { onTask: evaluation.onTask.dobjId });
            }
            evaluations.push(_evaluation);
        });
        if (evaluations.length > 0) {
            context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList: evaluations });
        }
        if (tasks.length > 0) {
            void context.dispatch(`${PRIVATE_TASK_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: tasks }, { root: true });
        }
    }
};
